import { ColliClient } from '@frontend/colli/api';
import { Colli, ColliType } from '@frontend/colli/types';
import { SlotRendering } from '@frontend/common-types';
import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { PackageStateName, PackageTriggerName, PackageType } from '@frontend/package/types';
import type { Package } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionTriggerName, TransactionUserType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import React, { useEffect, useState } from 'react';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../../components/slot-layout-cell';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { collectTransactionUserIds, syncTransactionUsers } from '../../utils/sync-transaction-users';
import { useScreen } from '../useScreen';

interface ViewProps {
    accountId?: string;
    spotId?: string;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlot: Slot | null;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    submitDisabled: boolean;
    isOpeningSlot: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;

    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
}

const PACKAGE_PROCESSING_POLL_ATTEMPTS = 30;
const PACKAGE_PROCESSING_POLL_INTERVAL_MS = 500;
const OPENABLE_DROP_PACKAGE_STATES = new Set([PackageStateName.PACKAGE_DROP_OFF_PENDING, PackageStateName.PACKAGE_DROP_OFF_STARTED]);

const wait = (duration: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, duration));

const waitForOpenableDropPackage = async (accountId: string, transactionId: string, packageId: string): Promise<Package | null> => {
    for (let attempt = 0; attempt < PACKAGE_PROCESSING_POLL_ATTEMPTS; attempt += 1) {
        const dropPackage = await PackageClient.fetchPackage(accountId, transactionId, packageId);
        if (OPENABLE_DROP_PACKAGE_STATES.has(dropPackage.state)) return dropPackage;
        if (dropPackage.state !== PackageStateName.PACKAGE_CREATED) return null;

        await wait(PACKAGE_PROCESSING_POLL_INTERVAL_MS);
    }

    return null;
};

const useFablabDropOffConfirmOpenSlot = (): ViewProps => {
    const { navigationState, store, updateWizardData, onBack, onSubmit: onContinue } = useScreen();
    const dropPkgWf = store?.functionalitySettings.find((setting) => setting.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
    const pickPkgWf = store?.functionalitySettings.find((setting) => setting.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
    const colliWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.COLLI_WORKFLOW_ID)?.value;
    const hasColliWorkflow = typeof colliWorkflowId === 'string' && colliWorkflowId.length > 0;

    const { transaction, syncTransaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store
    });

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState<number>(0);

    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [availableSlots, setAvailableSlots] = useState<Slot[] | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState<boolean>(false);
    const [isOpeningSlot, setIsOpeningSlot] = useState<boolean>(false);

    // Fetch spot and modules
    useEffect(() => {
        const accountId = navigationState.iot?.account_id;
        const spotId = navigationState.iot?.spot_id;

        if (accountId && spotId) {
            ModuleClient.fetchModules({ spot_id: spotId }).then((result) => {
                // Sort modules by name using ASCII comparison (handles both numeric and alphabetic: 1, 2, 3... or a, b, c...)
                const sortedModules = result.results.sort((a, b) => {
                    const indexA = a.index || '';
                    const indexB = b.index || '';
                    return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
                });
                setModules(sortedModules);
            });

            SlotClient.fetchSlots({ spot_id: spotId, size: '1000', index: '0' }).then((result) => {
                setAllSlots(result.results);
            });

            SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                account_id: accountId,
                application: store?.functionality?.application,
                feature: store?.functionality?.feature,
                functionality_id: store?.functionality?.id,
                transaction_type: transaction?.type
            }).then((result) => {
                setAvailableSlots(result.results);
            });
        }
    }, []);

    useEffect(() => {
        if (availableSlots && modules) {
            if (availableSlots.length > 0) {
                setSelectedSlot(availableSlots[0]);
                setCurrentModuleIndex(modules.findIndex((m) => m.id === availableSlots[0]?.module_id));
            }
        }
    }, [availableSlots, modules]);

    const currentModule = modules && modules.length > 0 ? modules[currentModuleIndex] : null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) {
                setCurrentModuleIndex(index);
            }
        }
        setSelectModuleModalOpen(false);
    };

    const slotComponent = (slotRendering: SlotRendering): React.ReactNode => {
        const slot = allSlots ? allSlots.find((s) => s.id === slotRendering.id) : null;
        const isHighlighted = slot?.id === selectedSlot?.id;
        const isAvailable = !!availableSlots?.find((s) => s.id === slot?.id);
        const slotNumber = slotLayoutDisplayNumber(slot);

        return (
            <SlotLayoutCell
                slotNumber={slotNumber}
                isSelected={!!isHighlighted}
                isDisabled={slot?.type === SlotType.TERMINAL}
                hasContent={isAvailable}
                onSelect={isAvailable && slot ? () => setSelectedSlot(slot) : undefined}
            />
        );
    };

    const onSubmit = async () => {
        if (!store || !selectedSlot || !dropPkgWf || !pickPkgWf || isOpeningSlot) return;

        setIsOpeningSlot(true);
        try {
            const updatedTransaction = await syncTransaction({
                spot_id: selectedSlot.spot_id,
                module_id: selectedSlot.module_id,
                slot_id: selectedSlot.id
            });

            const senderIds = collectTransactionUserIds(updatedTransaction.sender_ids, store.data.senders);
            const receiverIds = collectTransactionUserIds(updatedTransaction.receiver_ids, store.data.receivers);
            const [senders, receivers] = await Promise.all([
                senderIds.length > 0
                    ? syncTransactionUsers(updatedTransaction.account_id, updatedTransaction.id, TransactionUserType.SENDER, senderIds)
                    : Promise.resolve(null),
                receiverIds.length > 0
                    ? syncTransactionUsers(updatedTransaction.account_id, updatedTransaction.id, TransactionUserType.RECEIVER, receiverIds)
                    : Promise.resolve(null)
            ]);

            let createdColli: Colli | undefined;
            if (hasColliWorkflow) {
                createdColli = await ColliClient.postColli(updatedTransaction.account_id, updatedTransaction.id, {
                    label: `${updatedTransaction.tracking_number || updatedTransaction.id}-1`,
                    type: ColliType.STANDARD,
                    index: 1,
                    workflow_id: colliWorkflowId,
                    spot_id: selectedSlot.spot_id,
                    module_id: selectedSlot.module_id,
                    slot_id: selectedSlot.id
                });
            }

            const pkgInfo = {
                user_id: updatedTransaction.user_id,
                quantity: 1,
                spot_id: selectedSlot.spot_id,
                module_id: selectedSlot.module_id,
                slot_id: selectedSlot.id,
                blocking: false,
                ...(createdColli ? { colli_id: createdColli.id } : {})
            };
            const dropPackage = await PackageClient.postPackage(updatedTransaction.account_id, updatedTransaction.id, {
                ...pkgInfo,
                workflow_id: dropPkgWf,
                type: PackageType.DROP_OFF,
                priority: 2,
                application: updatedTransaction.application,
                feature: updatedTransaction.feature
            });
            await PackageClient.postPackage(updatedTransaction.account_id, updatedTransaction.id, {
                ...pkgInfo,
                workflow_id: pickPkgWf,
                type: PackageType.PICK_UP,
                priority: 1,
                application: updatedTransaction.application,
                feature: updatedTransaction.feature,
                dependent_package_id: dropPackage.id
            });

            updateWizardData({
                transaction: updatedTransaction,
                package: dropPackage,
                ...(createdColli ? { collis: [createdColli], packages: [dropPackage] } : {}),
                ...(senders ? { senders } : {}),
                ...(receivers ? { receivers } : {})
            });

            await TransactionWorkflowClient.triggerTransactionState(updatedTransaction.account_id, updatedTransaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });

            const processedDropPackage = await waitForOpenableDropPackage(updatedTransaction.account_id, updatedTransaction.id, dropPackage.id);
            if (!processedDropPackage) {
                Logger.warn('FabLab drop-off package was not ready to open after transaction processing', {
                    transactionId: updatedTransaction.id,
                    packageId: dropPackage.id
                });
                return;
            }

            updateWizardData({
                transaction: updatedTransaction,
                package: processedDropPackage,
                ...(createdColli ? { packages: [processedDropPackage] } : {})
            });

            if (processedDropPackage.state === PackageStateName.PACKAGE_DROP_OFF_PENDING) {
                await PackageWorkflowClient.triggerPackageState(updatedTransaction.account_id, updatedTransaction.id, processedDropPackage.id, {
                    trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                });
            }

            const nextStep = store.steps.find((step) => step.screen === ScreenType.FABLAB_DROP_OFF_CONFIRM_OPEN_SLOT && step.trigger === ScreenTrigger.SUBMIT);
            onContinue(nextStep);
        } finally {
            setIsOpeningSlot(false);
        }
    };

    return {
        accountId: navigationState.iot?.account_id || undefined,
        spotId: navigationState.iot?.spot_id || undefined,
        currentModule,
        modules,
        selectedSlot,
        setCurrentModule,
        slotComponent,
        submitDisabled: isOpeningSlot || !selectedSlot || !dropPkgWf || !pickPkgWf,
        isOpeningSlot,
        onSubmit,
        onBack,

        selectModuleModalOpen,
        setSelectModuleModalOpen
    };
};

export default useFablabDropOffConfirmOpenSlot;
